type AuthEventType = 'login' | 'logout';

interface AuthMessage {
  type: AuthEventType;
  timestamp: number;
}

const CHANNEL_NAME = 'auth';
const STORAGE_KEY = 'auth-broadcast';

let channel: BroadcastChannel | null = null;
let listener: ((event: StorageEvent) => void) | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    return channel;
  }
  return null;
}

/** Broadcast an auth event to other tabs. */
export function broadcastAuthEvent(type: AuthEventType): void {
  const message: AuthMessage = { type, timestamp: Date.now() };

  const bc = getChannel();
  if (bc) {
    bc.postMessage(message);
  } else {
    // localStorage fallback — writing triggers `storage` event in other tabs
    localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
  }
}

/** Listen for auth events from other tabs. Returns a cleanup function. */
export function onAuthEvent(callback: (type: AuthEventType) => void): () => void {
  const bc = getChannel();

  if (bc) {
    const handler = (event: MessageEvent<AuthMessage>) => {
      callback(event.data.type);
    };
    bc.addEventListener('message', handler);
    return () => bc.removeEventListener('message', handler);
  }

  // localStorage fallback
  listener = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const message: AuthMessage = JSON.parse(event.newValue);
      callback(message.type);
    } catch {
      // ignore malformed messages
    }
  };
  window.addEventListener('storage', listener);
  return () => {
    if (listener) {
      window.removeEventListener('storage', listener);
      listener = null;
    }
  };
}

/** Close the broadcast channel. */
export function cleanup(): void {
  if (channel) {
    channel.close();
    channel = null;
  }
  if (listener) {
    window.removeEventListener('storage', listener);
    listener = null;
  }
}
